import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeCoreTextbookSubtopicDetailViewQuery } from '__free_textbooks_generated__/FreeCoreTextbookSubtopicDetailViewQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import PageDescription from 'ms-helpers/PageDescription';
import PageTitle from 'ms-helpers/PageTitle';
import { FreeCoreTextbookSubtopicDetailViewTabs } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeCoreTextbookSubtopicDetailViewTabs';
import { FreeTextbookWorksheet } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeTextbookWorksheet';
import SEO from 'ms-pages/FreeTextbooks/seo';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import Switch from 'ms-pages/Teacher/components/Switch';
import type { CoreTextbookSubtopicActiveTab } from 'ms-pages/Textbooks/components/SubtopicDetailView/helpers';
import { validateCoreTextbookSubtopicActiveTab } from 'ms-pages/Textbooks/components/SubtopicDetailView/helpers';
import { SwitchWrapper } from 'ms-pages/Textbooks/components/elements';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import Separator from 'ms-ui-primitives/Separator';
import useQueryParam from 'ms-utils/hooks/useQueryParam';

import { FreeTextbookJsxLessonContainer } from './FreeTextbookJsxLessonContainer';
import { FreeTextbooksSubtopicViewQuestions } from '../FreeTextbooksSubtopicViewQuestions/FreeTextbooksSubtopicViewQuestions';

const MainContentWrapper = styled.div({
  background: colors.white,
  width: '100%',
  height: '100%',
});

const ContentWrapper = styled.div({
  // just a stopgap so that spinner/empty  don't render too close to the tabs
  // seems to give a bit of breath to the content too by the way
  marginTop: 4 * BASE_UNIT,
});

type Props = {
  subtopicId: string;
};

export function FreeCoreTextbookSubtopicDetailView(props: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeCoreTextbookSubtopicDetailView">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeCoreTextbookSubtopicDetailViewInner
            {...props}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeCoreTextbookSubtopicDetailViewInner({
  subtopicId,
  fetchKey,
}: Props & { fetchKey: string }) {
  const [activeTab, setActiveTab] =
    useQueryParam<CoreTextbookSubtopicActiveTab>(
      'coreTextbookSubtopicActiveTab',
      'solidifyLesson',
      validateCoreTextbookSubtopicActiveTab,
    );
  const data = useLazyLoadQuery<FreeCoreTextbookSubtopicDetailViewQuery>(
    graphql`
      query FreeCoreTextbookSubtopicDetailViewQuery($subtopicId: ID!) {
        subtopic(id: $subtopicId) {
          ...FreeTextbookJsxLessonContainer_subtopic
          worksheetJsx {
            transpiledJsx
          }
          title
        }
      }
    `,
    { subtopicId },
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and have sound types
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  const { subtopic } = data;
  if (subtopic == null) return <>Subtopic was not found</>;

  const worksheet = subtopic.worksheetJsx.transpiledJsx;

  return (
    <MainContentWrapper>
      <FreeCoreTextbookSubtopicDetailViewTabs />
      <ContentWrapper>
        {activeTab === 'solidifyLesson' && (
          <FreeTextbookJsxLessonContainer subtopicKey={subtopic} />
        )}
        {(activeTab === 'interactive' || activeTab === 'worksheet') && (
          <>
            <SwitchWrapper>
              <Switch<CoreTextbookSubtopicActiveTab>
                // 3/5 of the CoreTextbookSubtopicActiveTab members can't be switched to:
                // 'solidifyLesson', 'subtopicOverview', 'engageActivity'
                // These are not intended to be available in the free version of the
                // textbook apparently.
                // TODO: add a new FreeTextbookSubtopicActiveTab which is derived
                // via the Extract utility from the CoreTextbookSubtopicActiveTab
                // to document this intent more rigorously.
                options={[
                  {
                    label: 'Adaptive',
                    value: 'interactive',
                  },
                  {
                    label: 'Worksheet',
                    value: 'worksheet',
                  },
                ]}
                value={activeTab}
                onChange={value => {
                  setActiveTab(value);
                }}
                width={260}
              />
            </SwitchWrapper>
            <Separator size={4} />
            {activeTab === 'interactive' && (
              <>
                <PageTitle
                  separator=" | "
                  subtitles={SEO.SUBTOPIC_PRACTICE.title(subtopic.title)}
                />
                <PageDescription
                  description={SEO.SUBTOPIC_PRACTICE.description(
                    subtopic.title,
                  )}
                />
                <FreeTextbooksSubtopicViewQuestions subtopicId={subtopicId} />
              </>
            )}
            {worksheet != null && activeTab === 'worksheet' && (
              <>
                <PageTitle
                  subtitles={SEO.SUBTOPIC_WORKSHEET.title(subtopic.title)}
                />
                <PageDescription
                  description={SEO.SUBTOPIC_WORKSHEET.description(
                    subtopic.title,
                  )}
                />
                <FreeTextbookWorksheet jsxContent={worksheet} />
              </>
            )}
          </>
        )}
      </ContentWrapper>
    </MainContentWrapper>
  );
}
